/**
 * Banners and shields — the CLIENT half of their community adapter, plus their state check (pure, so
 * the editor can run it before saving and the server adapter in `./banner.server.ts` runs the same one).
 *
 * Both types share one state format (the banner editor's share hash) and differ only in `mode`, so
 * one adapter serves both.
 */
import type { LibraryItem } from '@/app/saved/lib/library';
import { decodeFromHash } from '@/app/tools/banner-editor/lib/exporter';
import { DESIGN_TYPES, capitalize, openInToolHref, type PublicDesign } from '../shared';
import type { ClientDesignAdapter } from './contract';

/**
 * Why a banner/shield state cannot be published, or null when it can.
 *
 * ⚠️ REJECT, never strip. A design with a custom name or lore is refused with a reason rather than
 * published with those fields removed: stripping would publish something that differs from what the
 * author saw, and a reviewer could not tell what had been taken out. Refusing them keeps every
 * published banner free of text except its title — and without text, its state is only colors and
 * patterns, which is what makes this type safe to share.
 */
export function bannerStateProblem(state: string): string | null {
  if (state.length === 0 || state.length > 8192) return 'That design could not be read.';
  const decoded = decodeFromHash(state);
  if (!decoded) return 'That design could not be read.';
  if (decoded.layers.length > 16) return 'That design has more layers than Minecraft allows.';
  // A plain banner is not a design — without this, a blank white banner went straight to the review queue.
  if (!decoded.layers.some(l => l.enabled)) return 'Add at least one pattern first — a plain banner or shield isn’t a design to share.';
  if (decoded.customName.trim() !== '' || decoded.lore.some(l => l.trim() !== '')) {
    return 'Remove the custom name and lore to publish — community designs are patterns only.';
  }
  return null;
}

/**
 * `Thumbnail` reads only `type` and `metadata.stateHash`; the other fields are placeholders for the
 * LibraryItem type, not data. Never pass this object to anything that opens, edits or saves a library
 * item — `source: 'element'` would make it look like one of the viewer's own builds.
 */
function toLibraryItem(design: PublicDesign): LibraryItem {
  const mode = design.type === 'shield' ? 'shield' : 'banner';
  return {
    uid: `design-${design.id}`,
    source: 'element',
    id: 0,
    type: mode,
    name: design.title,
    version: design.gameVersion,
    createdAt: design.createdAt,
    folder: null,
    tags: [],
    pinned: false,
    openPath: '',
    buttonLabel: '',
    summary: '',
    metadata: { stateHash: design.state, mode },
  };
}

export const bannerClient: ClientDesignAdapter = {
  toLibraryItem,
  openHref: design => openInToolHref(design.type, design.state, design.gameVersion),
  saveCopy: (saved, design) =>
    saved.saveBanner({
      name: design.title,
      stateHash: design.state,
      version: design.gameVersion ?? '',
      summary: `${capitalize(DESIGN_TYPES[design.type].singular)} design by ${design.authorUsername}`,
      mode: design.type === 'shield' ? 'shield' : 'banner',
    }),
  publishProblem: bannerStateProblem,
};
